import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { theme as antdTheme } from 'antd';
import JoditEditor from 'jodit-react';
import { ThemeContext } from '../../index';
import './RichContentEditor.css';

const RichContentEditor = ({
  interpretation,
  value,
  onChange,
  onBlur,
  readOnly = false,
  showMetadata = true,
  style = {},
  placeholder = "Enter HTML content...",
  height = "200px",
  loading = false
}) => {
  const editor = useRef(null);
  const [content, setContent] = useState('');
  const { themeConfig } = useContext(ThemeContext);
  const isDark = themeConfig?.algorithm === antdTheme.darkAlgorithm;

  useEffect(() => {
    if (value) {
      setContent(value);
    } else if (interpretation?.interpretationContent) {
      setContent(interpretation.interpretationContent);
    } else {
      setContent('');
    }
  }, [value, interpretation]);

  const config = useMemo(
    () => ({
      readonly: readOnly,
      placeholder: placeholder,
      height: 'auto',
      minHeight: 200,
      maxHeight: 600,
      theme: isDark ? 'dark' : 'default',
      buttons: [
        'source',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'ul',
        'ol',
        '|',
        'outdent',
        'indent',
        '|',
        'font',
        'fontsize',
        'brush',
        'paragraph',
        '|',
        'image',
        'table',
        'link',
        '|',
        'align',
        'undo',
        'redo',
        '|',
        'hr',
        'eraser',
        'copyformat',
        '|',
        'symbol',
        'fullsize',
        'print'
      ],
      buttonsXS: [
        'bold',
        'italic',
        '|',
        'ul',
        'ol',
        '|',
        'table',
        'image',
        '|',
        'source'
      ],
      uploader: {
        insertImageAsBase64URI: true
      },
      removeButtons: [],
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_clear_html',
      enter: 'P',
      toolbarAdaptive: true,
      toolbarSticky: false
    }),
    [readOnly, placeholder, isDark]
  );

  const handleBlur = (newContent) => {
    if (onChange) {
      onChange(newContent);
    }
    if (onBlur) {
      onBlur(newContent);
    }
  };

  const handleChange = (newContent) => {
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  };

  const renderMetadata = () => {
    if (!showMetadata || !interpretation) return null;

    return (
      <div className="interpretation-content-meta">
        {interpretation.interpretationTitle && (
          <div className="meta-item">
            <span className="meta-label">Title:</span>
            <span>{interpretation.interpretationTitle}</span>
          </div>
        )}
        {interpretation.interpretationCategory && (
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span>{interpretation.interpretationCategory}</span>
          </div>
        )}
        {interpretation.interpretationAuthor && (
          <div className="meta-item">
            <span className="meta-label">Author:</span>
            <span>{interpretation.interpretationAuthor}</span>
          </div>
        )}
        {interpretation.interpretationNotes && (
          <div className="meta-item">
            <span className="meta-label">Notes:</span>
            <span>{interpretation.interpretationNotes}</span>
          </div>
        )}
        {interpretation.isActive !== undefined && (
          <div className="meta-item">
            <span className="meta-label">Status:</span>
            <span className={`status ${interpretation.isActive ? 'active' : 'inactive'}`}>
              {interpretation.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        )}
        {interpretation.createdAt && (
          <div className="meta-item">
            <span className="meta-label">Created:</span>
            <span>{new Date(interpretation.createdAt).toLocaleDateString()}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="interpretation-content-container" style={style}>
      {renderMetadata()}

      {readOnly ? (
        <div
          className="interpretation-content-view"
          style={{
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            minHeight: height
          }}
        >
          {content ? (
            <div
              dangerouslySetInnerHTML={{ __html: content }}
              style={{
                lineHeight: '1.6'
              }}
            />
          ) : (
            <div style={{ color: '#999' }}>
              {placeholder}
            </div>
          )}
        </div>
      ) : (
        <div className="interpretation-content-editor jodit-wrapper">
          <JoditEditor
            ref={editor}
            value={content}
            config={config}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </div>
      )}
    </div>
  );
};

export default RichContentEditor;
